/**
 * Translation Mode Definitions — Single Source of Truth
 *
 * Each mode defines:
 * - id: Frontend identifier used in UI state and sessionStorage
 * - label: Display name shown in ModeSelector
 * - description: Short explanation shown below the mode label
 * - backendMode: The mode value sent to the backend API
 * - contextOptions: Intent-specific context buttons for this mode
 * - placeholder: Textarea placeholder text reflecting the mode's intent
 */

/** Backend mode values accepted by the translate API */
export type BackendMode = 'write' | 'understand' | 'be_understood';

/** Frontend mode identifiers */
export type FrontendMode = 'translate' | 'understand' | 'be_understood';

/** A single context option button */
export interface ContextOption {
  id: string;
  label: string;
  description: string;
}

/** Full mode definition */
export interface TranslationModeConfig {
  id: FrontendMode;
  label: string;
  description: string;
  backendMode: BackendMode;
  contextOptions: ContextOption[];
  placeholder: string;
}

/**
 * Maps a frontend mode to its backend API mode value.
 * "translate" on the frontend maps to "write" on the backend.
 */
export function mapFrontendMode(mode: FrontendMode): BackendMode {
  const modeConfig = TRANSLATION_MODES.find((m) => m.id === mode);
  if (!modeConfig) {
    // Fallback to 'write' for safety
    return 'write';
  }
  return modeConfig.backendMode;
}

/** All translation modes in display order */
export const TRANSLATION_MODES: TranslationModeConfig[] = [
  {
    id: 'translate',
    label: 'Translate',
    description: 'Standard translation between languages',
    backendMode: 'write',
    contextOptions: [
      { id: 'formal', label: 'Formal', description: 'Polite, respectful tone' },
      { id: 'casual', label: 'Casual', description: 'Relaxed, everyday speech' },
      { id: 'business', label: 'Business', description: 'Professional, corporate communication' },
      { id: 'academic', label: 'Academic', description: 'Scholarly, precise language' },
      { id: 'creative', label: 'Creative', description: 'Expressive, artistic freedom' },
    ],
    placeholder: 'Enter text to translate...',
  },
  {
    id: 'understand',
    label: 'Understand',
    description: 'Decode subtext, sarcasm, and cultural context',
    backendMode: 'understand',
    contextOptions: [
      { id: 'literal', label: 'Literal', description: 'Word-for-word meaning' },
      { id: 'subtext', label: 'Interpret Subtext', description: 'Read between the lines' },
      { id: 'sarcasm', label: 'Detect Sarcasm', description: 'Identify irony and sarcasm' },
      { id: 'cultural', label: 'Cultural Context', description: 'Explain cultural references and norms' },
    ],
    placeholder: 'Paste text you want to understand...',
  },
  {
    id: 'be_understood',
    label: 'Be Understood',
    description: 'Encode appropriate tone, formality, and cultural sensitivity',
    backendMode: 'be_understood',
    contextOptions: [
      { id: 'assertive', label: 'Assertive', description: 'Direct and confident' },
      { id: 'diplomatic', label: 'Diplomatic', description: 'Tactful and considerate' },
      { id: 'empathetic', label: 'Empathetic', description: 'Warm and understanding' },
      { id: 'collaborative', label: 'Collaborative', description: 'Team-oriented and inclusive' },
    ],
    placeholder: 'Enter what you want to communicate clearly...',
  },
];

/** Default mode on first load */
export const DEFAULT_MODE: FrontendMode = 'translate';

/** Look up a mode config by its frontend ID */
export function getModeConfig(mode: FrontendMode): TranslationModeConfig {
  const config = TRANSLATION_MODES.find((m) => m.id === mode);
  if (!config) {
    return TRANSLATION_MODES[0]; // fallback to translate
  }
  return config;
}
